import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";

const StoreContext = createContext();

export const useStore = () => {
    const context = useContext(StoreContext);
    if (!context) throw new Error('useStore must be used within a StoreProvider');
    return context;
};

const getRestType = (pathname) => {
    if (pathname === '/') return 'front';
    if (pathname.startsWith('/project/')) return 'project';
    return 'page';
};

const buildUrl = (restType, slug) => {
    let url = '/wp-json/tenet/v1/';
    switch (restType) {
        case 'front': url += 'front'; break;
        case 'project': url += `project/${slug}`; break;
        case 'page':
        default: url += `page/${slug}`; break;
    }
    return url;
};

export const StoreProvider = ({ children }) => {
    const location = useLocation();
    const params = useParams();

    const currentSlug = params.slug || '';
    const currentRestType = getRestType(location.pathname);

    const [state, setState] = useState({
        newPage: true,
        prevSlug: '/',
        slug: currentSlug,
        restType: currentRestType,
        lastRestType: null,
        route: location.pathname,
        posts: [],
        siteLoaded: false,
        pageLoaded: false,
        appError: '',
        global: { json: null, globalFetched: false },
        menu: { location: '/' },
    });

    const siteLoadedRef = useRef(state.siteLoaded);
    const pendingUrlRef = useRef(null);

    useEffect(() => {
        siteLoadedRef.current = state.siteLoaded;
    }, [state.siteLoaded]);

    const getGlobal = useCallback(async (url) => {
        if (!state.global.json && !state.global.globalFetched) {
            setState(prev => ({ ...prev, global: { ...prev.global, globalFetched: true } }));
            try {
                const response = await axios.get(url);
                setState(prev => ({ ...prev, global: { json: response.data[0], globalFetched: true } }));
            } catch {
                setState(prev => ({ ...prev, appError: 'Failed to load global data' }));
            }
        }
    }, [state.global.json, state.global.globalFetched]);

    const toggleMenu = useCallback(() => {
        const hasMenuClass = document.documentElement.classList.contains('state-menu-open');
        document.documentElement.classList.toggle('state-menu-open', !hasMenuClass);
    }, []);

    const getPosts = useCallback(async (url) => {
        if (pendingUrlRef.current === url) return; // prevent duplicate fetch while pending
        pendingUrlRef.current = url;

        try {

            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            const response = await axios.get(url);
            const postData = response.data;

            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';

            setState(prev => ({ ...prev, posts: postData }));

            if (postData[0]?.page_title) document.title = postData[0].page_title;
            if (postData[0]?.block && postData[0]?.block[0]?.type === 'splash') {
                document.documentElement.classList.add('state-splash-header', 'state-front-page');
            }

            if (!siteLoadedRef.current) {
                document.documentElement.classList.remove('state-splash-header');
                document.documentElement.classList.add('state-show-block');
                setTimeout(() => {
                    setState(prev => ({ ...prev, siteLoaded: true }));
                    setTimeout(() => {
                        document.documentElement.classList.add('state-page-loaded', 'state-site-loaded');
                    }, 0);
                }, 2000);
            } else {
                document.documentElement.classList.remove('state-splash-header');
                setTimeout(() => {
                    document.documentElement.classList.add('state-page-loaded', 'state-show-block');
                }, 100);
            }

            pendingUrlRef.current = null;
        
        } catch {
            setState(prev => ({ ...prev, appError: 'An unexpected error occurred' }));
            pendingUrlRef.current = null;
        }
    }, []);

    useEffect(() => {
        const newSlug = params.slug || '';
        const newRestType = getRestType(location.pathname);
        const newRoute = location.pathname;

        setState(prev => ({
            ...prev,
            newPage: prev.slug !== newSlug,
            prevSlug: prev.slug,
            slug: newSlug,
            lastRestType: prev.restType,
            restType: newRestType,
            route: newRoute,
        }));

        const url = buildUrl(newRestType, newSlug);

        const timeoutId = setTimeout(() => {
            getPosts(url);
        }, 500); // fade-out delay

        return () => clearTimeout(timeoutId);
    }, [location.pathname, location.hash, params.slug, getPosts]);

    return (
        <StoreContext.Provider value={{ ...state, getGlobal, toggleMenu }}>
            {children}
        </StoreContext.Provider>
    );
};

export const Provider = StoreProvider;
export const Consumer = StoreContext.Consumer;
